/**
 * Guide API layer — the in-app, read-only "how do I use this?" help assistant.
 *
 * Backend contract: ``apps.guide`` (``/api/v1/guide/``). Guide is NOT the AI
 * Copilot: it reads no personal data, has no tools, and takes no actions — it
 * only explains how to use the app. It is available only when the deployment is
 * configured (Anthropic key); when it isn't, ``available`` is false and the SPA
 * hides the chat bubble entirely.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api";
import { isAuthenticated } from "@/lib/auth";

export type GuideRole = "user" | "guide";
export type GuideRating = "" | "up" | "down";

export interface GuideMessage {
  id: string;
  role: GuideRole;
  body: string;
  rating: GuideRating;
  created_at: string;
}

/** The caller's thread plus whether Guide is reachable at all. */
export interface GuideThread {
  available: boolean;
  messages: GuideMessage[];
}

export interface GuideAskResult {
  user_message: GuideMessage;
  reply: GuideMessage;
}

const GUIDE_KEY = ["guide", "thread"] as const;

/** Load my thread + availability. Only fires when authenticated so it never
 *  races a 401 on the login screen. */
export function useGuide() {
  return useQuery<GuideThread>({
    queryKey: GUIDE_KEY,
    queryFn: () => api.get<GuideThread>("/guide/"),
    // Availability flips only on a deploy/config change — cache generously.
    staleTime: 10 * 60 * 1000,
    enabled: isAuthenticated(),
    retry: false,
  });
}

/** Ask a question. Persisted server-side (question + reply together) only on a
 *  successful answer. */
export function useAskGuide() {
  return useMutation({
    mutationFn: (body: string) =>
      api.post<GuideAskResult>("/guide/", { body }),
  });
}

/** Wipe my thread. */
export function useClearGuide() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<void>("/guide/clear/"),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: GUIDE_KEY }),
  });
}

/** Rate one of Dinnobot's replies 👍/👎 (or clear the vote with ""). Returns the
 *  updated message; the widget flips the vote in its local transcript. */
export function useRateGuideMessage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, rating }: { id: string; rating: GuideRating }) =>
      api.post<GuideMessage>(`/guide/${id}/rate/`, { rating }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: GUIDE_KEY }),
  });
}

/** Delete one turn. The server also removes its Q↔A partner and returns every
 *  id it deleted, so the widget prunes its transcript exactly. */
export function useDeleteGuideMessage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete<{ deleted_ids: string[] }>(`/guide/${id}/`),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: GUIDE_KEY }),
  });
}

/** Per-company AI Guide consumption — the billing counter. Admin-only on the
 *  backend; the hook simply surfaces a 403 as an error for the caller to gate. */
export interface GuideUsage {
  /** Current billing period this usage covers: [start, end) ISO dates. */
  period_start: string;
  period_end: string;
  questions: number;
  tokens_in: number;
  tokens_out: number;
  tokens_total: number;
  model: string;
  price_input_per_mtok: string;
  price_output_per_mtok: string;
  estimated_cost_usd: string;
  /** Gross client cost in PHP (raw AI cost × markup × USD→PHP rate). */
  estimated_cost_php: string;
  /** Free peso allowance per period; only the excess is billable. */
  free_allowance_php: string;
  /** Amount actually charged this period = max(0, cost − allowance). */
  billable_php: string;
}

export function useGuideUsage(enabled = true) {
  return useQuery<GuideUsage>({
    queryKey: ["guide", "usage"],
    queryFn: () => api.get<GuideUsage>("/guide/usage/"),
    enabled: enabled && isAuthenticated(),
    staleTime: 60 * 1000,
    retry: false,
  });
}

/** One AI feature's slice of a billing period (Dinnobot help bot / AI Assistant). */
export interface UsageFeature {
  feature: string;
  label: string;
  questions: number;
  tokens_in: number;
  tokens_out: number;
  cost_php: string;
}

/** One billing period (anchored on the 15th) with its by-feature breakdown. */
export interface UsagePeriod {
  period_start: string;
  period_end: string;
  is_current: boolean;
  questions: number;
  cost_php: string;
  free_allowance_php: string;
  billable_php: string;
  features: UsageFeature[];
}

/** Month-by-month AI billing history (admin-only). */
export function useGuideUsageHistory(enabled = true) {
  return useQuery<{ periods: UsagePeriod[] }>({
    queryKey: ["guide", "usage-history"],
    queryFn: () =>
      api.get<{ periods: UsagePeriod[] }>("/guide/usage/history/"),
    enabled: enabled && isAuthenticated(),
    staleTime: 60 * 1000,
    retry: false,
  });
}

/** The company-level AI master switch (Dinnobot + AI Assistant). ``can_manage``
 *  is true only for admins; the switch UI shows itself off that flag. */
export interface AiSettings {
  ai_enabled: boolean;
  can_manage: boolean;
}

export function useAiSettings(enabled = true) {
  return useQuery<AiSettings>({
    queryKey: ["guide", "ai-settings"],
    queryFn: () => api.get<AiSettings>("/guide/ai-settings/"),
    enabled: enabled && isAuthenticated(),
    staleTime: 60 * 1000,
    retry: false,
  });
}

/** Flip the org-wide AI switch (admins only). Invalidates availability + usage
 *  so the Dinnobot bubble and the usage card react immediately. */
export function useSetAiEnabled() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (aiEnabled: boolean) =>
      api.patch<AiSettings>("/guide/ai-settings/", { ai_enabled: aiEnabled }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["guide", "ai-settings"] });
      queryClient.invalidateQueries({ queryKey: GUIDE_KEY });
      queryClient.invalidateQueries({ queryKey: ["guide", "usage"] });
    },
  });
}
